import { QueryClient } from '@tanstack/react-query'
import { INotificationContentHandler } from '@/lib/handler/notification-content-handler'
import { NotificationType } from '@/types/notification'

/**
 * Backups Content Handler
 * Updates React Query cache when a backup run status changes via WebSocket notification.
 * Also updates the parent schedule detail cache so the embedded backups list stays in sync.
 */
export default class BackupsContentHandler implements INotificationContentHandler {
  async handle(notification: NotificationType, queryClient: QueryClient): Promise<void> {
    const { payload } = notification

    if (!payload || !payload.id) {
      return
    }

    const backupId = payload.id
    const status = payload.status
    const message = payload.message
    const file = payload.file

    // Update matching items in all backup list queries
    const listQueries = queryClient.getQueriesData({ queryKey: ['backups'] })

    listQueries.forEach(([queryKey, oldData]: [any, any]) => {
      if (!oldData || !oldData.items) {
        return
      }

      const itemExists = oldData.items.some((item: any) => item.id === backupId)

      if (itemExists) {
        queryClient.setQueryData(queryKey, {
          ...oldData,
          items: oldData.items.map((item: any) =>
            item.id === backupId ? { ...item, status, message, file, updatedAt: payload.updatedAt } : item
          )
        })
      }
    })

    // Update backup run detail query
    const detailQueries = queryClient.getQueriesData({ queryKey: ['backup', backupId] })

    detailQueries.forEach(([queryKey, oldData]: [any, any]) => {
      if (!oldData) {
        return
      }

      queryClient.setQueryData(queryKey, {
        ...oldData,
        status,
        message,
        file,
        updatedAt: payload.updatedAt
      })
    })

    // Update the embedded backups list inside the parent schedule detail cache.
    // Search ALL cached schedule-detail queries so we catch the case where
    // scheduleId is not present in the payload.
    const allScheduleQueries = queryClient.getQueriesData({ queryKey: ['backup-schedule'] })
    let updatedInScheduleCache = false

    allScheduleQueries.forEach(([queryKey, oldData]: [any, any]) => {
      if (!oldData || !oldData.backups) {
        return
      }

      const itemExists = oldData.backups.some((item: any) => item.id === backupId)

      if (itemExists) {
        updatedInScheduleCache = true
        queryClient.setQueryData(queryKey, {
          ...oldData,
          backups: oldData.backups.map((item: any) =>
            item.id === backupId ? { ...item, status, message, file, updatedAt: payload.updatedAt } : item
          )
        })
      }
    })

    // If the backup wasn't found in any schedule cache (new run), force a
    // refetch on all active schedule detail queries so the list refreshes.
    await queryClient.invalidateQueries({
      queryKey: ['backup-schedule'],
      refetchType: updatedInScheduleCache ? 'none' : 'active'
    })

    await queryClient.invalidateQueries({
      queryKey: ['backups'],
      refetchType: 'none'
    })

    await queryClient.invalidateQueries({
      queryKey: ['backup', backupId],
      refetchType: 'none'
    })
  }
}
